import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { ActivityComposer, ActivityRow } from '../components/ActivityKit';
import { Button, ErrorNote, Loading } from '../components/ui';
import { errorMessage } from '../lib/api';
import { listActivities } from '../lib/resources';
import type { Activity } from '../types';

const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

const MONTH_LABEL = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' });

/** Chave estável de dia local (não usar toISOString: ele converte para UTC). */
const dayKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;

/**
 * Grade do mês: começa no domingo da semana do dia 1 e vai até o sábado da
 * semana do último dia — sempre semanas completas.
 */
const buildGrid = (month: Date): Date[] => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());

  const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const end = new Date(last);
  end.setDate(last.getDate() + (6 - last.getDay()));

  const days: Date[] = [];
  for (let day = new Date(start); day <= end; day.setDate(day.getDate() + 1)) {
    days.push(new Date(day));
  }
  return days;
};

/** Calendário mensal das atividades. */
export function CalendarPage() {
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDay, setSelectedDay] = useState<string>(() => dayKey(new Date()));

  const grid = useMemo(() => buildGrid(month), [month]);

  const activitiesQuery = useQuery({
    queryKey: ['activities', 'calendar', month.getFullYear(), month.getMonth()],
    queryFn: () =>
      listActivities({
        // A grade mostra dias vizinhos, então o intervalo é o da grade, não o do mês.
        startDateFrom: new Date(
          grid[0].getFullYear(),
          grid[0].getMonth(),
          grid[0].getDate(),
          0,
          0,
          0,
        ).toISOString(),
        startDateTo: new Date(
          grid[grid.length - 1].getFullYear(),
          grid[grid.length - 1].getMonth(),
          grid[grid.length - 1].getDate(),
          23,
          59,
          59,
        ).toISOString(),
        take: 200,
      }),
  });

  const byDay = useMemo(() => {
    const map = new Map<string, Activity[]>();
    for (const activity of activitiesQuery.data?.data ?? []) {
      const key = dayKey(new Date(activity.startDate));
      map.set(key, [...(map.get(key) ?? []), activity]);
    }
    return map;
  }, [activitiesQuery.data]);

  const todayKey = dayKey(new Date());
  const selectedActivities = byDay.get(selectedDay) ?? [];

  const shiftMonth = (delta: number) =>
    setMonth((current) => new Date(current.getFullYear(), current.getMonth() + delta, 1));

  return (
    <div className="p-6">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Calendário</h1>
          <p className="text-sm capitalize text-slate-500">{MONTH_LABEL.format(month)}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => shiftMonth(-1)}>← Mês anterior</Button>
          <Button
            onClick={() => {
              const now = new Date();
              setMonth(new Date(now.getFullYear(), now.getMonth(), 1));
              setSelectedDay(dayKey(now));
            }}
          >
            Hoje
          </Button>
          <Button onClick={() => shiftMonth(1)}>Próximo mês →</Button>
        </div>
      </header>

      {activitiesQuery.isError && (
        <ErrorNote>
          {errorMessage(activitiesQuery.error, 'Falha ao carregar o calendário')}
        </ErrorNote>
      )}

      <div className="flex flex-col gap-6 xl:flex-row">
        <div className="flex-1 overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50 text-center text-[11px] uppercase tracking-wide text-slate-500">
            {WEEKDAYS.map((weekday) => (
              <div key={weekday} className="py-2">
                {weekday}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {grid.map((day) => {
              const key = dayKey(day);
              const dayActivities = byDay.get(key) ?? [];
              const abertas = dayActivities.filter((activity) => !activity.isCompleted).length;
              const isCurrentMonth = day.getMonth() === month.getMonth();
              const isSelected = key === selectedDay;

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedDay(key)}
                  className={`min-h-24 border-b border-r border-slate-100 p-2 text-left align-top transition hover:bg-slate-50 ${
                    isCurrentMonth ? '' : 'bg-slate-50/60 text-slate-400'
                  } ${isSelected ? 'ring-2 ring-inset ring-slate-900' : ''}`}
                >
                  <span
                    className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                      key === todayKey ? 'font-semibold text-white' : 'text-slate-600'
                    }`}
                    style={
                      key === todayKey ? { backgroundColor: 'var(--crm-primary)' } : undefined
                    }
                  >
                    {day.getDate()}
                  </span>

                  <div className="mt-1 space-y-0.5">
                    {dayActivities.slice(0, 3).map((activity) => (
                      <p
                        key={activity.id}
                        className={`truncate rounded px-1 text-[11px] ${
                          activity.isCompleted
                            ? 'text-slate-400 line-through'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                        title={activity.title}
                      >
                        {activity.title}
                      </p>
                    ))}
                    {dayActivities.length > 3 && (
                      <p className="px-1 text-[11px] text-slate-400">
                        +{dayActivities.length - 3} · {abertas} aberta(s)
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <aside className="w-full shrink-0 xl:w-96">
          <section className="rounded-xl border border-slate-200 bg-white">
            <header className="border-b border-slate-200 px-4 py-3">
              <h2 className="text-sm font-semibold text-slate-800">
                {new Date(`${selectedDay}T12:00:00`).toLocaleDateString('pt-BR', {
                  weekday: 'long',
                  day: '2-digit',
                  month: 'long',
                })}
              </h2>
              <p className="text-xs text-slate-500">
                {selectedActivities.length} atividade(s) no dia
              </p>
            </header>

            {activitiesQuery.isLoading ? (
              <Loading />
            ) : (
              <div className="divide-y divide-slate-100">
                {selectedActivities.map((activity) => (
                  <ActivityRow key={activity.id} activity={activity} />
                ))}
                {selectedActivities.length === 0 && (
                  <p className="px-4 py-6 text-center text-xs text-slate-400">
                    Nada agendado neste dia.
                  </p>
                )}
              </div>
            )}
          </section>

          <section className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Agendar
            </h2>
            <ActivityComposer compact />
          </section>
        </aside>
      </div>
    </div>
  );
}
